/**
 * useChatDensity — V2.0.14 (Feb 15, 2026)
 *
 * Shared chat density preference (cozy | compact) persisted in localStorage
 * so all 3 chat surfaces (TeamTalk, DaddyTalk, CandidateMessages) stay in
 * sync and remember the user's choice across sessions.
 *
 * `compact` shrinks bubble padding, gap between messages, and font size so
 * power-users on small phones can see roughly ~40 % more messages per
 * screen without needing to scroll.
 */

import { useEffect, useState } from "react";
import { Rows, ListDashes } from "@phosphor-icons/react";

const STORAGE_KEY = "chat_density";
const DEFAULT = "cozy";

export function useChatDensity() {
    const [density, setDensity] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || DEFAULT; }
        catch { return DEFAULT; }
    });
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, density); } catch {}
    }, [density]);
    return [density, setDensity];
}

/** Compact/cozy toggle used inside the ConvoHeader of each chat surface. */
export function ChatDensityToggle({ density, setDensity, testid = "chat-density-toggle" }) {
    const isCompact = density === "compact";
    return (
        <button
            type="button"
            data-testid={testid}
            title={isCompact ? "Switch to cozy" : "Switch to compact"}
            aria-label="Toggle chat density"
            onClick={() => setDensity(isCompact ? "cozy" : "compact")}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 text-ink/60 transition-colors"
        >
            {isCompact
                ? <ListDashes size={14} weight="bold" />
                : <Rows size={14} weight="bold" />
            }
        </button>
    );
}
